import { AlertCircle, Brain, Target, TrendingUp, Zap } from "lucide-react";
import React, { useState } from "react";
import { Button, Input, SectionHeader, Select } from "../../components";
import analyticsService from "../../services/analyticsService";
import { cn } from "../../utils/cn";
import Card from "../ui/Card";

interface PredictionData {
  success: boolean;
  predicted_engagement_rate?: number;
  predicted_category?: string;
  feature_importance?: Record<string, number>;
  recommendations?: string[];
  message?: string;
}

const EngagementPredictor: React.FC = () => {
  const [prediction, setPrediction] = useState<PredictionData | null>(null);
  const [loading, setLoading] = useState(false);
  const [predictionError, setPredictionError] = useState<string | null>(null);
  const [testPost, setTestPost] = useState({
    content: "Descubra como a IA está transformando o marketing digital! 🚀",
    platform: "linkedin",
    scheduled_time: "09:00",
    hashtags: ["#IA", "#Marketing", "#Transformacao"],
    has_image: true,
    call_to_action: "Saiba mais!",
  });

  const maxContentLength = 280;

  const predictEngagement = async () => {
    setLoading(true);
    setPredictionError(null);
    setPrediction(null);
    try {
      const data = await analyticsService.predictEngagement(testPost);
      if (data.success && data.predicted_engagement_rate !== undefined) {
        setPrediction(data);
      } else {
        setPredictionError(
          data.message || "A IA não retornou uma predição válida.",
        );
      }
    } catch (error) {
      console.error("Erro ao predizer engajamento:", error);
      setPredictionError(
        error instanceof Error
          ? error.message
          : "Não foi possível calcular a predição. Verifique sua conexão e tente novamente.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Configurator */}
      <Card className="lg:col-span-1 p-3 border-purple-200 bg-white/60 backdrop-blur-md shadow-xs h-fit">
        <SectionHeader
          icon={<Target className="h-5 w-5" />}
          title="Lab de Predição"
          subtitle="Simulação de Performance IA"
        />

        <div className="space-y-2">
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">
              Corpo da Publicação
            </label>
            <textarea
              value={testPost.content}
              onChange={(e) =>
                setTestPost({
                  ...testPost,
                  content: e.target.value.slice(0, maxContentLength),
                })
              }
              className="w-full p-3 bg-gray-50/50 border border-purple-200 rounded-xl text-sm font-medium focus:ring-0 focus:border-purple-300 focus:bg-white transition-all min-h-[160px] resize-none shadow-inner"
              placeholder="Insira o texto para análise semântica..."
            />
            <div className="flex justify-end px-2">
              <span className="text-[10px] font-black text-purple-600/50 uppercase tracking-widest">
                {testPost.content.length}/{maxContentLength}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Select
              label="Rede Alvo"
              value={testPost.platform}
              onChange={(value) =>
                setTestPost({ ...testPost, platform: value })
              }
              options={[
                { value: "linkedin", label: "LinkedIn" },
                { value: "facebook", label: "Facebook" },
                { value: "instagram", label: "Instagram" },
              ]}
            />
            <Input
              label="Agendamento"
              type="time"
              value={testPost.scheduled_time}
              onChange={(e) =>
                setTestPost({ ...testPost, scheduled_time: e.target.value })
              }
            />
          </div>

          <Input
            label="Fluxo de Hashtags"
            placeholder="separadas por vírgula..."
            value={testPost.hashtags.join(", ")}
            onChange={(e) =>
              setTestPost({
                ...testPost,
                hashtags: e.target.value
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
          />

          <div className="flex items-center gap-4 p-3 bg-purple-50/30 rounded-lg border border-purple-200 group cursor-pointer hover:bg-purple-50 transition-colors">
            <input
              type="checkbox"
              className="w-5 h-5 rounded-lg border-purple-200 text-purple-600 focus:ring-purple-500 transition-all cursor-pointer"
              checked={testPost.has_image}
              onChange={(e) =>
                setTestPost({ ...testPost, has_image: e.target.checked })
              }
              id="has_image_pred"
            />
            <label
              htmlFor="has_image_pred"
              className="text-xs font-bold text-purple-900 cursor-pointer select-none"
            >
              Presença de Mídia Visual
            </label>
          </div>

          <Button
            onClick={predictEngagement}
            disabled={loading}
            className="w-full h-11 bg-purple-600 hover:bg-purple-700 shadow-xs font-black uppercase text-[10px] tracking-widest rounded-lg group transition-all"
          >
            {loading ? (
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                <span>Calculando...</span>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Zap
                  size={14}
                  className="group-hover:scale-125 transition-all"
                />
                <span>Iniciar Simulação</span>
              </div>
            )}
          </Button>
        </div>
      </Card>

      {/* Results Section */}
      <div className="lg:col-span-2 space-y-2">
        {prediction ? (
          <>
            <Card className="p-3 bg-[#0f0f12] shadow-xs relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-purple-600/20 blur-[120px] rounded-full -mr-48 -mt-48 animate-pulse"></div>
              <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-2 items-center">
                <div className="flex flex-col items-center md:items-start text-center md:text-left space-y-2">
                  <div className="p-4 bg-white/5 rounded-xl backdrop-blur-md border border-white/10 w-fit">
                    <TrendingUp size={40} className="text-purple-400" />
                  </div>
                  <div>
                    <h4 className="text-[10px] font-black uppercase tracking-[0.4em] text-purple-400 mb-2">
                      Engajamento Projetado
                    </h4>
                    <div className="text-7xl font-black tracking-tighter text-white">
                      {typeof prediction.predicted_engagement_rate ===
                        "number" &&
                      !Number.isNaN(prediction.predicted_engagement_rate)
                        ? `${(prediction.predicted_engagement_rate * 100).toFixed(1)}`
                        : "0.0"}
                      <span className="text-purple-500">%</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-center md:items-end justify-center h-full space-y-2">
                  <div
                    className={cn(
                      "px-3 py-3 rounded-full text-xs font-black uppercase tracking-widest backdrop-blur-md border-2",
                      prediction.predicted_category === "excelente"
                        ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20 shadow-lg shadow-emerald-500/10"
                        : prediction.predicted_category === "alto"
                          ? "bg-blue-500/10 text-blue-400 border-blue-500/20"
                          : "bg-amber-500/10 text-amber-400 border-amber-500/20",
                    )}
                  >
                    Status: {prediction.predicted_category}
                  </div>
                  <p className="text-xs font-bold text-gray-500 max-w-[200px] text-center md:text-right leading-relaxed italic">
                    Baseado no modelo de predição treinado com o histórico da
                    sua conta.
                  </p>
                </div>
              </div>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Card className="p-3 border-purple-200 bg-white">
                <div className="flex items-center gap-4 mb-3">
                  <div className="p-2.5 bg-purple-50 rounded-xl">
                    <Brain size={18} className="text-purple-600" />
                  </div>
                  <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                    Diretrizes de Otimização
                  </h4>
                </div>
                <div className="space-y-2">
                  {prediction.recommendations?.map((rec, i) => (
                    <div
                      key={i}
                      className="flex gap-3 p-3 bg-gray-50/50 border border-purple-200 rounded-lg hover:border-purple-300 transition-colors group"
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-2 shrink-0 group-hover:scale-150 transition-transform" />
                      <p className="text-xs font-bold text-gray-700 leading-relaxed italic">
                        "{rec}"
                      </p>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="p-3 border-purple-200 bg-white">
                <div className="flex items-center gap-4 mb-3">
                  <div className="p-2.5 bg-gray-50 rounded-xl">
                    <Zap size={18} className="text-amber-500" />
                  </div>
                  <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                    Vetores de Tração
                  </h4>
                </div>
                <div className="space-y-2">
                  {Object.entries(prediction.feature_importance || {})
                    .sort(([, a], [, b]) => {
                      const aVal =
                        typeof a === "number" && !Number.isNaN(a) ? a : 0;
                      const bVal =
                        typeof b === "number" && !Number.isNaN(b) ? b : 0;
                      return bVal - aVal;
                    })
                    .map(([key, val]) => {
                      const valNum =
                        typeof val === "number" && !Number.isNaN(val) ? val : 0;
                      return (
                        <div key={key} className="space-y-2">
                          <div className="flex justify-between items-end px-1">
                            <span className="text-[10px] font-black uppercase tracking-wider text-gray-400 capitalize">
                              {key.replace("_", " ")}
                            </span>
                            <span className="text-xs font-black text-gray-900">
                              {(valNum * 100).toFixed(0)}%
                            </span>
                          </div>
                          <div className="h-1.5 bg-gray-50 rounded-full overflow-hidden border border-purple-200">
                            <div
                              className="h-full bg-purple-500 rounded-full transition-all duration-1000"
                              style={{ width: `${valNum * 100}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              </Card>
            </div>
          </>
        ) : predictionError ? (
          <div className="min-h-[380px] flex flex-col items-center justify-center p-3 border-2 border-dashed border-rose-200 rounded-xl bg-rose-50/20">
            <div className="w-20 h-20 bg-white rounded-xl flex items-center justify-center shadow-xs border border-rose-200 mb-3">
              <AlertCircle size={36} className="text-rose-400" />
            </div>
            <h4 className="text-xl font-black tracking-tight text-rose-500 uppercase">
              Falha na Predição
            </h4>
            <p className="text-sm font-semibold text-rose-400 mt-3 max-w-[360px] text-center leading-relaxed">
              {predictionError}
            </p>
            <button
              onClick={() => setPredictionError(null)}
              className="mt-3 px-3 py-2 text-xs font-black uppercase tracking-widest border border-rose-200 text-rose-500 rounded-lg hover:bg-rose-50 transition-colors"
            >
              Tentar novamente
            </button>
          </div>
        ) : (
          <div className="min-h-[380px] flex flex-col items-center justify-center p-3 border-2 border-dashed border-purple-200 rounded-xl bg-gray-50/30">
            <div className="w-20 h-20 bg-white rounded-xl flex items-center justify-center shadow-xs border border-purple-200 mb-3 group-hover:rotate-12 transition-transform">
              <Target size={36} className="text-gray-200" />
            </div>
            <h4 className="text-xl font-black tracking-tight text-gray-400 uppercase">
              Simulador IA
            </h4>
            <p className="text-sm font-semibold text-gray-400 mt-3 max-w-[320px] text-center leading-relaxed">
              Ajuste os parâmetros no console lateral para quantificar o
              engajamento potencial do seu conteúdo.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EngagementPredictor;
